import {registerLocaleData} from '@angular/common';
import localeEn from '@angular/common/locales/en';
import localeFr from '@angular/common/locales/fr';
import localeFrCA from '@angular/common/locales/fr-CA';
import {APP_INITIALIZER, ErrorHandler, Injector, NgModule} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {platformBrowserDynamic} from '@angular/platform-browser-dynamic';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {Router} from '@angular/router';
import {JwtHelperService} from '@auth0/angular-jwt';
import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {TranslateStore} from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import {LoggerModule, NGXLogger, NgxLoggerLevel} from 'ngx-logger';
import {environment} from '../assets/environments/environment';
import {commonProperties} from '../assets/environments/environment.common';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {AuthenticationModule} from './authentication/authentication.module';
import {CoreModule} from './core/core.module';
import {SharedModule} from './shared/shared.module';

registerLocaleData(localeEn);
registerLocaleData(localeFrCA);
registerLocaleData(localeFr);

export function initConfig(injector: Injector, logger: NGXLogger) {
  return () => {
      if (!environment.production) {
        const updatedConfig = logger.getConfigSnapshot();
        updatedConfig.level = NgxLoggerLevel.ERROR;
        logger.updateConfig(updatedConfig);
      }
     const jwtHelper = injector.get(JwtHelperService);
      const router = injector.get(Router);
      const token = localStorage.getItem(commonProperties.token);
      if (token) {
        if (jwtHelper.isTokenExpired()) {
          // console.log('app initializer : token expired');
          localStorage.removeItem('access_token');
          router.navigate(['login']);
        }

        const xhr = new XMLHttpRequest();
        xhr.open('POST', environment.api_root + commonProperties.tokenValidation);
        xhr.setRequestHeader('Content-Type', 'application/json;charset=UTF-8');

        xhr.addEventListener('readystatechange', () => {
          if (xhr.readyState === XMLHttpRequest.DONE && xhr.status !== 204) {
            localStorage.removeItem('access_token');
            router.navigate(['login']);
          }
        });

        xhr.send(JSON.stringify({
          token,
          type: 'BUYER'
        }));
      }
  };
}

Sentry.init({
  dsn: 'https://984f00ba826bd2e9ce12d979bee93d56@o4509627437350912.ingest.de.sentry.io/4509688811946064',
  // Adds request headers and IP for users, for more info visit:
  // https://docs.sentry.io/platforms/javascript/guides/angular/configuration/options/#sendDefaultPii
  sendDefaultPii: true,
  integrations: [
    // Registers and configures the Tracing integration,
    // which automatically instruments your application to monitor its
    // performance, including custom Angular routing instrumentation
    Sentry.browserTracingIntegration(),
  ],
  environment: environment.env,
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for tracing.
  // We recommend adjusting this value in production
  // Learn more at
  // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
  tracesSampleRate: 1.0,
  // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
  tracePropagationTargets: ['localhost', /^https:\/\/api\.qa\.isupplier\.fr/, /^https:\/\/api\.demo\.isupplier\.fr/, /^https:\/\/api\.staging\.isupplier\.fr/, /^https:\/\/api\.isupplier\.fr/],
});

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    CoreModule,
    SharedModule,
    AuthenticationModule,
    LoggerModule.forRoot({
      level: NgxLoggerLevel.DEBUG,
      timestampFormat: 'yyyy-MM-dd HH:mm:ss',
    }),
    FontAwesomeModule,
  ],
  providers: [
    TranslateStore,
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initConfig,
      multi: true,
      deps: [Injector, NGXLogger, Sentry.TraceService]
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}

platformBrowserDynamic()
  .bootstrapModule(AppModule)
  .catch((err) => console.error(err));
